import { CalendarCheck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { Callout } from '@/components/patterns/callout';
import { CampoData } from '@/components/patterns/date-picker';
import { DataTable, Paginacao, type Coluna } from '@/components/patterns/data-table';
import { FilterBar, FilterBusca, FilterFlagsInstalacao, FilterLimpar } from '@/components/patterns/filter-bar';
import { PermissionButton } from '@/components/patterns/permission-button';
import { VersaoGuiasSelect } from '@/components/patterns/versao-guias-select';
import { InstalacaoFlags, PapelBadge, SaudeBadge } from '@/components/patterns/status-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { dataSource, JANELA_AGENDAMENTO, RETENCAO_TTL_SEGUNDOS } from '@/data';
import { mensagemDoErro } from '@/data/http';
import type {
  Agendamento,
  GuiaVersao,
  HorarioDisponivel,
  Instalacao,
  PaginaInstalacoes,
  RetencaoHorario,
  VersoesInstalacao,
} from '@/data/types';
import { useAsyncAction } from '@/hooks/use-async-action';
import { useRealtimeAgendamentos, type EventoAgendamento } from '@/hooks/use-realtime-agendamentos';
import { useResource } from '@/hooks/use-resource';
import { formatarDataExtenso, hojeISO, plural, somarDias, texto } from '@/lib/format';
import {
  BLOQUEAR_AGENDAMENTO_FIM_DE_SEMANA,
  flagDoFiltro,
  ordenarPorFamilia,
  podeAgendar,
  proximoDiaUtil,
} from '@/lib/rules';
import { cn } from '@/lib/utils';

/**
 * Agendar atualização.
 *
 * No app original esta tela era um grid multiselect à esquerda e um bloco de
 * quatro campos à direita, todos habilitados ou desabilitados por uma condição
 * declarada no layout JSON. Funcionava, mas não explicava: o usuário descobria
 * que não podia agendar ao encontrar um botão cinza.
 *
 * Aqui o fluxo é explícito em três etapas, e cada indisponibilidade tem nome:
 *   1. quais instalações — linhas inelegíveis ficam visíveis com o motivo;
 *   2. qual versão — a lista é a INTERSEÇÃO das versões aplicáveis a todas as
 *      instalações marcadas, porque um lote só faz sentido numa versão comum;
 *   3. quando — a capacidade de cada horário continua limitando a escolha,
 *      mas não é mais exibida como número: um horário sem vaga suficiente
 *      para o lote simplesmente não aparece na lista, em vez de aparecer
 *      desabilitado com a contagem de vagas. A hora escolhida (ex.: 09:00) é
 *      um horário de fila, não o disparo de verdade: instalações do mesmo
 *      horário são espaçadas por pelo menos 2 minutos (o worker dispara cada
 *      uma na sua vez), e é esse horário real — vindo da retenção — que
 *      aparece no Resumo, não a hora cheia crua.
 */
export function AgendarPage() {
  const navegar = useNavigate();

  // Busca, filtros e paginação são no servidor: quem administra centenas de
  // instalações não pode depender de baixar o escopo inteiro de uma vez.
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [filtroAna, setFiltroAna] = useState<string | null>(null);
  const [filtroInterno, setFiltroInterno] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(25);

  // Debounce só da busca textual — os selects aplicam na hora.
  useEffect(() => {
    const id = window.setTimeout(() => setBuscaAplicada(busca.trim()), 300);
    return () => window.clearTimeout(id);
  }, [busca]);

  // Qualquer mudança de filtro invalida a página atual (um offset além do
  // fim do resultado novo voltaria vazio).
  useEffect(() => {
    setPagina(1);
  }, [buscaAplicada, filtroAna, filtroInterno, tamanhoPagina]);

  const recursoInstalacoes = useResource<PaginaInstalacoes>(
    useCallback(
      (signal: AbortSignal) =>
        dataSource.listInstalacoesPaginado(
          {
            busca: buscaAplicada || undefined,
            ana: flagDoFiltro(filtroAna),
            interno: flagDoFiltro(filtroInterno),
            limit: tamanhoPagina,
            offset: (pagina - 1) * tamanhoPagina,
          },
          signal,
        ),
      [buscaAplicada, filtroAna, filtroInterno, pagina, tamanhoPagina],
    ),
    [buscaAplicada, filtroAna, filtroInterno, pagina, tamanhoPagina],
  );

  // Instalações DESTA página — controladora + controladas aparecem agrupadas
  // dentro da página (a ordem do servidor é por nome, então uma família pode
  // se dividir entre páginas vizinhas).
  const instalacoes = useMemo(
    () => ordenarPorFamilia(recursoInstalacoes.dados?.itens ?? []),
    [recursoInstalacoes.dados],
  );
  const totalNoEscopo = recursoInstalacoes.dados?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(totalNoEscopo / tamanhoPagina));
  const filtrosAtivos = [buscaAplicada !== '', filtroAna !== null, filtroInterno !== null].filter(Boolean).length;

  // ---- Trava visual: quem já tem atualização programada ou em andamento, ou
  // está sendo agendada por outra pessoa agora, some da lista antes de virar
  // erro no envio. 'Executando' conta como pendente também — uma instalação
  // que já começou a atualizar continua bloqueando um novo agendamento tanto
  // quanto uma ainda 'Programado' (ver `instalacoes_com_agendamento_ativo`
  // no backend, que aplica a mesma regra). ----
  const recursoAgendamentos = useResource<Agendamento[]>(
    useCallback((signal: AbortSignal) => dataSource.listAgendamentos(signal), []),
    [],
    { intervaloAtualizacaoMs: 60_000 },
  );
  const instalacoesComAgendamentoPendente = useMemo(
    () =>
      new Set(
        (recursoAgendamentos.dados ?? [])
          .filter((item) => item.status === 'Programado' || item.status === 'Executando')
          .map((item) => item.instalacaoId),
      ),
    [recursoAgendamentos.dados],
  );

  // instalação → holdId de quem está retendo agora (não eu) — atualizado ao
  // vivo pelo WebSocket, com autolimpeza no mesmo TTL do backend (2 min) caso
  // a mensagem de liberação se perca (reconexão, etc.).
  const [travadasPorOutros, setTravadasPorOutros] = useState<Map<string, string>>(new Map());

  // Lote livre: qualquer conjunto de instalações onde o usuário é administrador,
  // não precisa ser uma família só (controladora + suas controladas) — o backend
  // autoriza cada instalação alvo individualmente. A seleção atravessa páginas e
  // filtros, por isso guarda a instalação inteira (não só o id): a página onde
  // ela foi marcada pode já não estar na tela.
  const [selecionadasPorId, setSelecionadasPorId] = useState<Map<string, Instalacao>>(new Map());
  const selecionadas = useMemo(() => new Set(selecionadasPorId.keys()), [selecionadasPorId]);
  const [versao, setVersao] = useState<string | null>(null);
  const [data, setData] = useState<string>(() => proximoDiaUtil(somarDias(hojeISO(), 1)));
  const [hora, setHora] = useState<string | null>(null);

  const alvos = useMemo(() => Array.from(selecionadasPorId.values()), [selecionadasPorId]);

  // ---- Etapa 2: versões aplicáveis a TODAS as instalações marcadas ----
  // Cada instalação vem com suas próprias guias (LD/QA/Master — guias abaixo
  // do estágio atual dela já não vêm do backend, ver `GuiaVersao`). Uma guia
  // só sobrevive na interseção se TODAS as instalações do lote tiverem essa
  // MESMA guia (mesma `chave`) — do contrário o lote nem chega a comparar
  // versão, porque uma delas já não enxerga aquele estágio.
  const recursoVersoes = useResource<VersoesInstalacao[]>(
    useCallback(
      async (signal: AbortSignal) => {
        if (alvos.length === 0) return [];
        return Promise.all(alvos.map((item) => dataSource.listVersoes(item.id, signal)));
      },
      [alvos],
    ),
    [alvos.map((item) => item.id).join('|')],
  );

  const guiasComuns = useMemo<GuiaVersao[]>(() => {
    const [primeira, ...resto] = recursoVersoes.dados ?? [];
    if (!primeira) return [];
    return primeira.guias.flatMap((guia) => {
      const correspondentes = resto.map((instalacao) => instalacao.guias.find((item) => item.chave === guia.chave));
      if (correspondentes.some((item) => !item)) return [];
      const listas = [guia, ...(correspondentes as GuiaVersao[])].map((item) =>
        item.versoes.filter((versao) => !versao.semAtualizacoesDisponiveis),
      );
      if (listas.some((lista) => lista.length === 0)) return [];
      const [primeiraLista, ...restoListas] = listas;
      const versoesComuns = primeiraLista.filter((candidata) =>
        restoListas.every((lista) => lista.some((item) => item.id === candidata.id)),
      );
      return versoesComuns.length > 0 ? [{ ...guia, versoes: versoesComuns }] : [];
    });
  }, [recursoVersoes.dados]);

  // Limpa a versão escolhida quando ela deixa de ser aplicável ao lote.
  useEffect(() => {
    if (versao && !guiasComuns.some((guia) => guia.versoes.some((item) => item.id === versao))) setVersao(null);
  }, [guiasComuns, versao]);

  // ---- Etapa 3: disponibilidade do dia escolhido ----
  const recursoHorarios = useResource<HorarioDisponivel[]>(
    useCallback((signal: AbortSignal) => dataSource.listDisponibilidade(data, signal), [data]),
    [data],
    // Fallback de segurança — o WebSocket abaixo é quem normalmente traz a
    // atualização quase na hora; isso só cobre o caso do socket estar caído.
    { intervaloAtualizacaoMs: 60_000 },
  );
  const horarios = recursoHorarios.dados ?? [];
  // Só entram na lista os horários com vaga para o lote inteiro — um horário
  // sem capacidade suficiente simplesmente não aparece, em vez de aparecer
  // desabilitado com a contagem de vagas (ver JSDoc do componente). O horário
  // já escolhido (`hora`) sempre fica visível mesmo que a própria retenção
  // tenha zerado a disponibilidade dele — senão o botão selecionado sumiria
  // da grade assim que a retenção fosse confirmada.
  const necessariasParaOLote = Math.max(alvos.length, 1);
  const horariosDisponiveis = horarios.filter(
    (horario) => horario.disponivel >= necessariasParaOLote || horario.horario === hora,
  );

  // ---- Retenção temporária: selecionar já segura a vaga, não só confirmar ----
  const [retencao, setRetencao] = useState<RetencaoHorario | null>(null);
  const [erroRetencao, setErroRetencao] = useState<string | null>(null);
  const [segundosRestantes, setSegundosRestantes] = useState<number | null>(null);
  const retencaoAtivaRef = useRef<{ data: string; hora: string; holdId: string } | null>(null);
  // Cresce a cada clique — se o resultado da retenção em segundo plano chegar
  // depois que a pessoa já trocou de novo, o número não bate e o resultado é
  // descartado (libera a vaga que ganhou à toa). Sem isso, cliques rápidos
  // trocando de horário poderiam aplicar uma resposta antiga por cima da nova.
  const selecaoIdRef = useRef(0);
  // Marcado de forma SÍNCRONA, antes do POST de retenção sair — o eco do meu
  // próprio "retido" chega pelo WebSocket e pode chegar antes da resposta do
  // POST (que é quando `retencaoAtivaRef` seria preenchido); sem isso, minha
  // própria seleção pisca como "travada por outra pessoa" por uma fração de
  // segundo.
  const minhasInstalacoesEmRetencaoRef = useRef<Set<string>>(new Set());

  // Capacidade por horário é disputada por várias pessoas ao mesmo tempo, e o
  // mesmo vale pra travar a instalação em si: sem isso, cada um só descobre
  // que a outra reservou/travou primeiro ao tentar.
  useRealtimeAgendamentos((evento: EventoAgendamento) => {
    recursoHorarios.atualizarEmSilencio();

    if (evento.tipo === 'criado' || evento.tipo === 'cancelado') {
      recursoAgendamentos.atualizarEmSilencio();
    }

    const instalacoesDoEvento = Array.isArray(evento.instalacoes)
      ? (evento.instalacoes as unknown[]).filter((item): item is string => typeof item === 'string')
      : [];
    const souEu =
      instalacoesDoEvento.length > 0 &&
      instalacoesDoEvento.every((id) => minhasInstalacoesEmRetencaoRef.current.has(id));

    if (evento.tipo === 'retido' && !souEu && instalacoesDoEvento.length > 0) {
      const holdId = String(evento.holdId);
      setTravadasPorOutros((atual) => {
        const proximo = new Map(atual);
        for (const id of instalacoesDoEvento) proximo.set(id, holdId);
        return proximo;
      });
      // Autolimpeza no mesmo TTL do backend — se a mensagem de liberação se
      // perder (reconexão do WS, etc.), a trava visual não fica presa pra sempre.
      window.setTimeout(() => {
        setTravadasPorOutros((atual) => {
          const proximo = new Map(atual);
          for (const id of instalacoesDoEvento) if (proximo.get(id) === holdId) proximo.delete(id);
          return proximo;
        });
      }, RETENCAO_TTL_SEGUNDOS * 1000);
    }

    if (evento.tipo === 'retencao_liberada' && instalacoesDoEvento.length > 0) {
      const holdId = String(evento.holdId);
      setTravadasPorOutros((atual) => {
        const proximo = new Map(atual);
        for (const id of instalacoesDoEvento) if (proximo.get(id) === holdId) proximo.delete(id);
        return proximo;
      });
    }

    // Capacidade mudou na Administração — o backend já apagou TODA retenção
    // em andamento (a minha e a de todo mundo), então não faz sentido tentar
    // liberar a minha de novo no servidor (`liberarRetencaoAtual` faria
    // isso); só limpa o estado local e avisa.
    if (evento.tipo === 'capacidade_alterada') {
      setTravadasPorOutros(new Map());
      if (retencaoAtivaRef.current) {
        retencaoAtivaRef.current = null;
        minhasInstalacoesEmRetencaoRef.current = new Set();
        setRetencao(null);
        setSegundosRestantes(null);
        toast.info('A capacidade de agendamento foi atualizada. Selecione o horário novamente.');
      }
    }
  });

  const liberarRetencaoAtual = useCallback(() => {
    const atual = retencaoAtivaRef.current;
    retencaoAtivaRef.current = null;
    minhasInstalacoesEmRetencaoRef.current = new Set();
    setRetencao(null);
    setSegundosRestantes(null);
    if (atual) {
      // Melhor esforço: se falhar, a vaga só libera quando os 2 min do TTL
      // passarem sozinhos — não é um erro que a pessoa precise ver.
      void dataSource.liberarRetencao(atual.holdId).catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    setHora(null);
    liberarRetencaoAtual();
  }, [data, liberarRetencaoAtual]);

  // Libera a retenção se a pessoa sair da tela sem confirmar nem trocar de horário.
  useEffect(() => () => liberarRetencaoAtual(), [liberarRetencaoAtual]);

  // F5/fechar aba destroem o JS antes do cleanup acima rodar — sem isso, a
  // retenção só some quando os 2 min do TTL passarem sozinhos no backend.
  // `pagehide` (não `beforeunload`) é o evento confiável pra isso, e
  // `melhorEsforco` (fetch `keepalive`) garante que o navegador tenta entregar
  // mesmo com a página já fechando.
  useEffect(() => {
    function aoSairDaPagina() {
      const atual = retencaoAtivaRef.current;
      if (atual) void dataSource.liberarRetencao(atual.holdId, { melhorEsforco: true }).catch(() => undefined);
    }
    window.addEventListener('pagehide', aoSairDaPagina);
    return () => window.removeEventListener('pagehide', aoSairDaPagina);
  }, []);

  // Contagem regressiva local — o TTL de verdade é no backend (Redis); isso só
  // espelha pra pessoa quanto tempo falta, e limpa a seleção quando estoura.
  useEffect(() => {
    if (!retencao) return;
    const expiraEmMs = new Date(retencao.expiraEm).getTime();
    const atualizar = () => {
      const restante = Math.max(0, Math.round((expiraEmMs - Date.now()) / 1000));
      setSegundosRestantes(restante);
      if (restante <= 0) {
        retencaoAtivaRef.current = null;
        minhasInstalacoesEmRetencaoRef.current = new Set();
        setRetencao(null);
        setHora(null);
        setErroRetencao('Sua reserva temporária desse horário expirou. Escolha novamente.');
        recursoHorarios.recarregar();
      }
    };
    atualizar();
    const id = window.setInterval(atualizar, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retencao]);

  /**
   * Botão comum: aperta/desaperta ou troca na hora, sem esperar a rede — a
   * retenção de verdade (`reterHorario`) acontece em segundo plano. Se a
   * pessoa clicar em outro horário antes dessa retenção voltar, o resultado
   * atrasado é descartado (e a vaga que ganhou à toa é liberada de novo);
   * se der errado (perdeu a vaga pra outra pessoa), a seleção volta atrás e
   * mostra o motivo — só então a pessoa nota, nunca antes disso.
   */
  function selecionarHorario(horario: HorarioDisponivel) {
    const jaAtivo = hora === horario.horario;
    const idSelecao = ++selecaoIdRef.current;
    const retencaoAnterior = retencaoAtivaRef.current;
    const instalacoesAlvo = alvos.map((item) => item.id);

    setErroRetencao(null);
    setHora(jaAtivo ? null : horario.horario);
    retencaoAtivaRef.current = null;
    setRetencao(null);
    setSegundosRestantes(null);
    // Síncrono, antes do POST: fecha a janela onde o eco do meu próprio
    // "retido" (WebSocket) chegaria antes da resposta e pareceria de outra pessoa.
    minhasInstalacoesEmRetencaoRef.current = jaAtivo ? new Set() : new Set(instalacoesAlvo);

    void (async () => {
      if (retencaoAnterior) {
        await dataSource.liberarRetencao(retencaoAnterior.holdId).catch(() => undefined);
      }

      if (!jaAtivo) {
        try {
          const nova = await dataSource.reterHorario({
            data,
            hora: horario.horario,
            instalacoesAlvo,
          });
          if (selecaoIdRef.current === idSelecao) {
            retencaoAtivaRef.current = { data, hora: horario.horario, holdId: nova.holdId };
            setRetencao(nova);
          } else {
            // A pessoa já trocou de novo enquanto isso resolvia — essa vaga não serve mais.
            minhasInstalacoesEmRetencaoRef.current = new Set();
            void dataSource.liberarRetencao(nova.holdId).catch(() => undefined);
          }
        } catch (causa) {
          if (selecaoIdRef.current === idSelecao) {
            minhasInstalacoesEmRetencaoRef.current = new Set();
            setErroRetencao(
              mensagemDoErro(causa) ??
                (causa instanceof Error ? causa.message : `Não foi possível reservar o horário ${horario.horario}.`),
            );
            setHora(null);
          }
        }
      }

      recursoHorarios.atualizarEmSilencio();
    })();
  }

  const criacao = useAsyncAction({
    sucesso:
      alvos.length > 1 ? `Atualização agendada para ${alvos.length} instalações.` : 'Atualização agendada.',
    contextoDoErro: 'Não foi possível agendar a atualização',
    aoConcluir: () => navegar('/agendamentos'),
  });

  // ---- Elegibilidade por linha ----
  function contextoDe(instalacao: Instalacao) {
    return {
      agendamentoPendente: instalacoesComAgendamentoPendente.has(instalacao.id),
      travadaPorOutro: travadasPorOutros.has(instalacao.id),
    };
  }

  function motivoInelegivel(instalacao: Instalacao): string | null {
    const regra = podeAgendar(instalacao, contextoDe(instalacao));
    return regra.permitido ? null : regra.motivo;
  }

  const colunas: Array<Coluna<Instalacao>> = [
    {
      id: 'nome',
      cabecalho: 'Instalação',
      ordenarPor: (item) => item.nome,
      celula: (item) => {
        const contexto = contextoDe(item);
        const regra = podeAgendar(item, contexto);
        const travada = contexto.agendamentoPendente || contexto.travadaPorOutro;
        return (
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className={cn('font-bold', !regra.permitido && 'text-on-surface-muted')}>{item.nome}</span>
              <InstalacaoFlags ana={item.ana} interno={item.interno} />
            </span>
            <span
              className={cn(
                'text-cell font-normal',
                !travada && !regra.permitido && regra.severidade === 'atencao' ? 'text-warning-text' : 'text-on-surface-muted',
              )}
            >
              {/* Travada: mantém o subtítulo normal — o motivo já está no
                  tooltip do cadeado, não precisa tomar a linha inteira. */}
              {travada || regra.permitido ? texto(item.cliente?.nome, 'Cliente sem dado') : regra.motivo}
            </span>
          </div>
        );
      },
    },
    {
      id: 'codigo',
      cabecalho: 'Código',
      largura: '150px',
      ordenarPor: (item) => item.codigo ?? '',
      celula: (item) => <span className="font-narrow text-cell text-on-surface-muted">{texto(item.codigo)}</span>,
    },
    { id: 'papel', cabecalho: 'Papel', largura: '150px', celula: (item) => <PapelBadge valor={item.papel} /> },
    {
      id: 'versao',
      cabecalho: 'Versão atual',
      largura: '130px',
      ordenarPor: (item) => item.versaoAtual,
      celula: (item) => <span className="font-narrow text-[15px] font-bold tabular-nums">{texto(item.versaoAtual)}</span>,
    },
    { id: 'saude', cabecalho: 'Saúde', largura: '130px', celula: (item) => <SaudeBadge valor={item.saude} /> },
  ];

  const elegiveis = instalacoes.filter((item) => !motivoInelegivel(item));

  // ---- Validação do envio ----
  const versaoEscolhida = guiasComuns.flatMap((guia) => guia.versoes).find((item) => item.id === versao) ?? null;
  const horarioEscolhido = horarios.find((item) => item.horario === hora) ?? null;

  // Horário REAL de disparo, não só a hora cheia escolhida: instalações do
  // mesmo horário são espaçadas por pelo menos 2 minutos (ver
  // `RetencaoHorario`). Só existe depois que a retenção confirma — antes
  // disso (ou se a retenção falhou) mostra a hora cheia mesmo, sem estimativa.
  const horarioRealLabel = retencao
    ? retencao.horarioRealInicio === retencao.horarioRealFim
      ? retencao.horarioRealInicio
      : `${retencao.horarioRealInicio}–${retencao.horarioRealFim}`
    : hora;

  const impedimento = (() => {
    if (alvos.length === 0) return 'Selecione ao menos uma instalação para agendar.';
    // Re-checa AGORA, não só no momento em que a instalação foi marcada na
    // etapa 1 (`elegiveis` só filtra a lista pra seleção, uma vez): entre
    // marcar e confirmar, outra pessoa pode ter começado a agendar a mesma
    // instalação (`travadasPorOutros`, via WebSocket) ou ela pode ter
    // ganhado um agendamento pendente nesse meio-tempo. Sem isso, nada
    // aqui barra o "Confirmar agendamento" quando isso acontece — o
    // servidor também garante (ver `instalacoes_travadas_por_outro` no
    // backend), mas a pessoa merece saber ANTES de tentar, não só no erro.
    const alvoTravado = alvos.map((item) => motivoInelegivel(item)).find((motivo) => motivo !== null);
    if (alvoTravado) return alvoTravado;
    if (recursoVersoes.estado === 'carregando') return 'Aguarde a lista de versões aplicáveis carregar.';
    if (guiasComuns.length === 0) {
      return alvos.length > 1
        ? 'Não há uma versão comum a todas as instalações selecionadas. Agende em grupos menores.'
        : `${alvos[0].nome} já está na versão mais recente; não há atualização a aplicar.`;
    }
    if (!versaoEscolhida) return 'Escolha a versão que será aplicada.';
    if (!data) return 'Informe a data do agendamento.';
    if (!horarioEscolhido) return 'Escolha um horário dentro da janela de manutenção.';
    if (horarioEscolhido.disponivel < alvos.length) {
      return `O horário ${horarioEscolhido.horario} não comporta mais as ${plural(alvos.length, 'instalação selecionada', 'instalações selecionadas')}. Escolha outro horário.`;
    }
    return null;
  })();

  return (
    <PageBody>
      <PageHeader
        eyebrow="Atualizações"
        titulo="Agendar atualização"
        descricao={`Escolha as instalações, a versão e um horário dentro da janela de manutenção (${JANELA_AGENDAMENTO.inicio}–${JANELA_AGENDAMENTO.fim}).`}
      />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        {/* ---------- Etapa 1 ---------- */}
        <EtapaCard
          numero={1}
          titulo="Instalações a atualizar"
          semRespiro
          apoio={
            <span>
              {instalacoes.length === 0
                ? filtrosAtivos > 0
                  ? 'Nenhuma instalação encontrada com esses filtros.'
                  : 'Nenhuma instalação no escopo.'
                : elegiveis.length === 0
                  ? 'Nenhuma instalação desta página está elegível para agendamento agora.'
                  : `${plural(elegiveis.length, 'instalação elegível', 'instalações elegíveis')} nesta página`}
              {` · ${plural(totalNoEscopo, 'instalação', 'instalações')}${filtrosAtivos > 0 ? ' com os filtros' : ' no escopo'}`}
              {selecionadasPorId.size > 0 ? ` · ${plural(selecionadasPorId.size, 'selecionada', 'selecionadas')}` : ''}
            </span>
          }
        >
            <div className="border-b border-border bg-bg-subtle px-4 py-3">
              <FilterBar>
                <FilterBusca
                  id="filtro-busca-agendar"
                  label="Buscar instalação"
                  valor={busca}
                  aoMudar={setBusca}
                  placeholder="Nome, código, cliente ou região"
                />
                <FilterFlagsInstalacao
                  idBase="filtro-agendar"
                  ana={filtroAna}
                  aoMudarAna={setFiltroAna}
                  interno={filtroInterno}
                  aoMudarInterno={setFiltroInterno}
                  mostrarAna={recursoInstalacoes.dados?.temAna ?? false}
                  mostrarInterno={recursoInstalacoes.dados?.temInterno ?? false}
                />
                <FilterLimpar
                  ativo={filtrosAtivos > 0}
                  quantidade={filtrosAtivos}
                  aoLimpar={() => {
                    setBusca('');
                    setBuscaAplicada('');
                    setFiltroAna(null);
                    setFiltroInterno(null);
                  }}
                />
              </FilterBar>
            </div>
            <DataTable
              rotulo="Instalações elegíveis para atualização"
              itens={instalacoes}
              colunas={colunas}
              chave={(item) => item.id}
              estado={recursoInstalacoes.estado}
              erro={recursoInstalacoes.erro}
              aoTentarDeNovo={recursoInstalacoes.recarregar}
              selecionadas={selecionadas}
              bloqueada={motivoInelegivel}
              aoAlternarSelecao={(id, marcado) =>
                setSelecionadasPorId((atual) => {
                  const proximo = new Map(atual);
                  const instalacao = instalacoes.find((item) => item.id === id);
                  if (marcado && instalacao) proximo.set(id, instalacao);
                  else proximo.delete(id);
                  return proximo;
                })
              }
              // "Selecionar todas" age só sobre a página visível: marca/desmarca
              // as elegíveis dela e preserva o que já estava marcado em outras páginas.
              aoAlternarTodas={(marcado) =>
                setSelecionadasPorId((atual) => {
                  const proximo = new Map(atual);
                  for (const item of elegiveis) {
                    if (marcado) proximo.set(item.id, item);
                    else proximo.delete(item.id);
                  }
                  return proximo;
                })
              }
              vazio={
                filtrosAtivos > 0
                  ? {
                      titulo: 'Nenhuma instalação encontrada',
                      descricao: 'Ajuste a busca ou os filtros para ver outras instalações do escopo.',
                    }
                  : {
                      titulo: 'Nenhuma instalação no escopo',
                      descricao: 'Este usuário não tem nenhuma instalação vinculada no Diretório.',
                    }
              }
            />
            {recursoInstalacoes.estado === 'pronto' ? (
              <div className="border-t border-border px-4 py-2.5">
                <Paginacao
                  pagina={pagina}
                  totalPaginas={totalPaginas}
                  aoMudar={setPagina}
                  tamanhoPagina={tamanhoPagina}
                  aoMudarTamanho={setTamanhoPagina}
                  tamanhosPagina={[10, 25, 50, 100]}
                />
              </div>
            ) : null}
        </EtapaCard>

        {/* ---------- Etapas 2 e 3 ---------- */}
        <div className="flex flex-col gap-6">
          <EtapaCard numero={2} titulo="Versão">
              {alvos.length === 0 ? (
                <Callout>Selecione ao menos uma instalação para ver as versões aplicáveis.</Callout>
              ) : recursoVersoes.estado === 'carregando' ? (
                <Callout>Carregando as versões aplicáveis às instalações selecionadas…</Callout>
              ) : recursoVersoes.estado === 'erro' ? (
                <Callout
                  tom="erro"
                  acao={
                    <Button variant="outline" size="sm" onClick={recursoVersoes.recarregar}>
                      Tentar de novo
                    </Button>
                  }
                >
                  {recursoVersoes.erro?.message ?? 'Não foi possível carregar as versões.'}
                </Callout>
              ) : guiasComuns.length === 0 ? (
                <Callout tom="alerta" kicker="Sem versão comum">
                  {alvos.length > 1
                    ? 'As instalações selecionadas não compartilham nenhuma versão de destino. Agende em grupos menores.'
                    : `${alvos[0].nome} já está na versão mais recente; não há atualização disponível.`}
                </Callout>
              ) : (
                <Field
                  id="campo-versao"
                  label="Versão de destino"
                  obrigatorio
                  apoio={
                    alvos.length > 1
                      ? `Mostrando apenas versões aplicáveis às ${alvos.length} instalações selecionadas.`
                      : undefined
                  }
                >
                  {(props) => (
                    <VersaoGuiasSelect
                      id={props.id}
                      describedBy={props['aria-describedby']}
                      valor={versao}
                      aoMudar={setVersao}
                      guias={guiasComuns}
                      placeholder="Escolher versão"
                    />
                  )}
                </Field>
              )}
          </EtapaCard>

          <EtapaCard numero={3} titulo="Data e horário">
              <Field
                id="campo-data"
                label="Data do agendamento"
                obrigatorio
                apoio={data ? formatarDataExtenso(data) : undefined}
              >
                {(props) => (
                  <CampoData
                    {...props}
                    valor={data}
                    min={hojeISO()}
                    bloquearFinsDeSemana={BLOQUEAR_AGENDAMENTO_FIM_DE_SEMANA}
                    aoMudar={setData}
                  />
                )}
              </Field>

              <fieldset className="flex flex-col gap-2">
                <legend className="text-label text-on-surface">
                  Horário{' '}
                  <span className="text-danger" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">(obrigatório)</span>
                </legend>
                {!versaoEscolhida ? (
                  <Callout tom="alerta">Escolha a versão na etapa 2 antes de escolher o horário.</Callout>
                ) : recursoHorarios.estado === 'carregando' ? (
                  <Callout>Consultando os horários disponíveis…</Callout>
                ) : recursoHorarios.estado === 'erro' ? (
                  <Callout
                    tom="erro"
                    acao={
                      <Button variant="outline" size="sm" onClick={recursoHorarios.recarregar}>
                        Tentar de novo
                      </Button>
                    }
                  >
                    {recursoHorarios.erro?.message ?? 'Não foi possível consultar a disponibilidade.'}
                  </Callout>
                ) : horarios.length === 0 ? (
                  <Callout tom="alerta">Nenhum horário de manutenção foi publicado para esta data.</Callout>
                ) : horariosDisponiveis.length === 0 ? (
                  <Callout tom="alerta">
                    Nenhum horário desta data tem espaço para {plural(necessariasParaOLote, 'instalação selecionada', 'instalações selecionadas')}. Tente outra data ou um lote menor.
                  </Callout>
                ) : (
                  <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {horariosDisponiveis.map((horario) => {
                      const ativo = hora === horario.horario;
                      return (
                        <li key={horario.id}>
                          <button
                            type="button"
                            aria-pressed={ativo}
                            onClick={() => void selecionarHorario(horario)}
                            className={cn(
                              'flex min-h-11 w-full items-center justify-center rounded-button border px-2 py-1.5 transition-colors duration-fast ease-standard',
                              ativo
                                ? 'border-primary bg-primary text-on-primary shadow-rest'
                                : 'border-border bg-bg-surface text-on-surface hover:border-primary hover:bg-primary-wash',
                            )}
                          >
                            <span className="font-narrow text-[17px] font-bold leading-none tabular-nums">
                              {horario.horario}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {erroRetencao ? <Callout tom="alerta">{erroRetencao}</Callout> : null}
                {retencao && segundosRestantes !== null ? (
                  <Callout tom={segundosRestantes <= 20 ? 'alerta' : undefined}>
                    Horário reservado para {horarioRealLabel} — confirme em {Math.floor(segundosRestantes / 60)}:
                    {String(segundosRestantes % 60).padStart(2, '0')}, antes que expire.
                  </Callout>
                ) : null}
              </fieldset>
          </EtapaCard>

          {/* ---------- Resumo e confirmação ---------- */}
          <EtapaCard titulo="Resumo" destacado>
              <ul className="flex flex-col divide-y divide-border-subtle text-body">
                <li className="flex items-baseline justify-between gap-3 py-1.5 first:pt-0">
                  <span className="text-on-surface-muted">Instalações</span>
                  <span className="text-right font-bold">
                    {alvos.length === 0 ? 'Nenhuma selecionada' : plural(alvos.length, 'instalação', 'instalações')}
                  </span>
                </li>
                <li className="flex items-baseline justify-between gap-3 py-1.5">
                  <span className="text-on-surface-muted">Versão</span>
                  <span className="text-right font-bold">{versaoEscolhida?.nome ?? 'A definir'}</span>
                </li>
                <li className="flex items-baseline justify-between gap-3 py-1.5">
                  <span className="text-on-surface-muted">Quando</span>
                  <span className="text-right font-bold">
                    {hora ? `${formatarDataExtenso(data)} às ${horarioRealLabel}` : formatarDataExtenso(data)}
                  </span>
                </li>
                <li className="flex items-baseline justify-between gap-3 py-1.5 last:pb-0">
                  <span className="text-on-surface-muted">Modo</span>
                  <span className="text-right">
                    {alvos.length > 1 ? (
                      <Badge tone="primary">Lote</Badge>
                    ) : (
                      <Badge tone="neutral">Instalação única</Badge>
                    )}
                  </span>
                </li>
              </ul>

              {alvos.length > 0 ? (
                <ul className="flex flex-wrap gap-1.5">
                  {alvos.map((item) => (
                    <li key={item.id}>
                      <Badge tone="neutral">{item.nome}</Badge>
                    </li>
                  ))}
                </ul>
              ) : null}

              {impedimento ? (
                <Callout tom="alerta" kicker="Falta para concluir">
                  {impedimento}
                </Callout>
              ) : null}

              {/* Hard rule 1: esta é a única ação primária da tela. */}
              <PermissionButton
                variant="primary"
                className="w-full"
                permitido={!impedimento}
                motivo={impedimento ?? ''}
                loading={criacao.executando}
                onClick={() => {
                  if (impedimento || !versaoEscolhida || !horarioEscolhido) return;
                  // O confirmar consome a retenção (o backend a libera de todo
                  // jeito) — solta a referência local pra não tentar de novo.
                  const holdId = retencaoAtivaRef.current?.holdId ?? null;
                  retencaoAtivaRef.current = null;
                  minhasInstalacoesEmRetencaoRef.current = new Set();
                  void criacao.executar(() =>
                    dataSource.criarAgendamento({
                      versao: versaoEscolhida.id,
                      data,
                      hora: horarioEscolhido.horario,
                      instalacoesAlvo: alvos.map((item) => item.id),
                      instalacaoAcesso: alvos[0]?.id ?? '',
                      holdId,
                    }),
                  );
                }}
              >
                <CalendarCheck aria-hidden="true" />
                Confirmar agendamento
              </PermissionButton>
          </EtapaCard>
        </div>
      </div>
    </PageBody>
  );
}

/**
 * Cartao de etapa do fluxo de agendamento.
 *
 * O marcador numerado e o do sustenta-ai-app (Playbook passo a passo): disco de
 * 24px em azul, numero em PT Sans Narrow. Aqui ele e informacao, nao
 * decoracao - a ordem importa, porque cada etapa depende da anterior para saber
 * o que oferecer. O cartao de Resumo nao recebe numero justamente por isso: ele
 * nao e uma etapa, e o que as tres produziram.
 */
function EtapaCard({
  numero,
  titulo,
  apoio,
  destacado = false,
  semRespiro = false,
  children,
}: {
  numero?: number;
  titulo: string;
  apoio?: ReactNode;
  destacado?: boolean;
  /** Para conteudo que encosta na borda (uma tabela), sem padding interno. */
  semRespiro?: boolean;
  children: ReactNode;
}) {
  return (
    <Card as="section" destacado={destacado}>
      <CardHeader destacado={destacado} className="flex-nowrap items-start">
        {numero !== undefined ? (
          <span
            aria-hidden="true"
            className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary font-narrow text-[13px] font-bold text-on-primary"
          >
            {numero}
          </span>
        ) : null}
        <div className="flex min-w-0 flex-col gap-0.5">
          <CardTitle>
            {numero !== undefined ? <span className="sr-only">Etapa {numero}: </span> : null}
            {titulo}
          </CardTitle>
          {apoio ? <CardDescription>{apoio}</CardDescription> : null}
        </div>
      </CardHeader>
      <CardContent semRespiro={semRespiro}>{children}</CardContent>
    </Card>
  );
}
